import {
	CircleCheck,
	CircleAlert,
	Sparkles,
	TriangleAlert,
	Cable,
	LucideIcon,
} from 'lucide-react'

import { ZoneStateStatus } from '@/core/api/entities/zone/state'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'

import { getHasSmartIntegration } from '../utils/common'

export interface ZoneStatusBadge {
	label: string
	className: string
	icon: LucideIcon
}

export interface ZoneStatusInfo {
	statusBadges: ZoneStatusBadge[]
	hasSmartIntegration: boolean
	hasWarnings: boolean
	warningsCount: number
	status: ZoneStateStatus
}

export const getBasicStatusBadge = (
	status: ZoneStateStatus,
	t: (key: string) => string
): ZoneStatusBadge => {
	switch (status) {
		case ZoneStateStatus.ONLINE:
			return {
				label: t('card.online'),
				className:
					'bg-status-success/20 text-status-success border-status-success',
				icon: CircleCheck,
			}
		case ZoneStateStatus.OFFLINE:
			return {
				label: t('card.offline'),
				className:
					'bg-status-inactive/20 text-status-inactive border-status-inactive',
				icon: CircleAlert,
			}
		case ZoneStateStatus.NOT_INSTALLED:
			return {
				label: t('card.notInstalled'),
				className: 'bg-muted text-muted-foreground border-border',
				icon: Cable,
			}
		case ZoneStateStatus.NOT_REACTIVATED:
			return {
				label: t('card.notReactivated'),
				className: 'bg-muted text-muted-foreground border-border',
				icon: Cable,
			}
		default:
			return {
				label: 'Unknown',
				className: 'bg-muted text-muted-foreground border-border',
				icon: Cable,
			}
	}
}

export const useZoneStatus = (zone: Zone): ZoneStatusInfo => {
	const { t } = useTranslation('zones')

	const status = zone.state?.status || ZoneStateStatus.NOT_INSTALLED
	const hasWarnings = (zone.state?.warnings?.length || 0) > 0
	const hasSmartIntegration = getHasSmartIntegration(zone)
	const warningsCount = zone.state?.warnings?.length || 0

	const badges: ZoneStatusBadge[] = []

	switch (status) {
		// Warning state is an ONLINE + warning, so we still need to show the online badge
		case ZoneStateStatus.ONLINE:
		case ZoneStateStatus.WARNING:
			badges.push({
				label: t('card.online'),
				className:
					'bg-status-success/20 text-status-success border-status-success',
				icon: CircleCheck,
			})
			break
		case ZoneStateStatus.OFFLINE:
			badges.push({
				label: t('card.offline'),
				className:
					'bg-status-inactive/20 text-status-inactive border-status-inactive',
				icon: CircleAlert,
			})
			break
	}

	if (hasSmartIntegration) {
		badges.push({
			label: t('card.smart'),
			className: 'bg-status-info/20 text-status-info border-status-info',
			icon: Sparkles,
		})
	}

	if (hasWarnings) {
		badges.push({
			label: `${warningsCount} ${warningsCount > 1 ? t('card.alerts') : t('card.alert')}`,
			className:
				'bg-status-warning/20 text-status-warning border-status-warning/30',
			icon: TriangleAlert,
		})
	}

	return {
		statusBadges: badges,
		hasSmartIntegration,
		hasWarnings,
		warningsCount,
		status,
	}
}
